import { DetectedMentions } from '@omni/ai/features/detected-mentions';
import { KnownCustomer } from './dictation-resolution';

/**
 * What the review opens on when a note becomes a meeting. Pure and Angular-free
 * so the rules can be tested directly.
 *
 * The rep confirms every detail that lands on a meeting, so the review has to be
 * able to open on a note the detector said nothing about — no agent configured,
 * a request that failed, or a reading that came back empty. The note itself is
 * always there to show, which is what these seeds are for.
 */

/** The note fields a review is seeded from; a stored note and a fresh capture both fit. */
export interface NoteSeedSource {
  name?: string | null;
  transcript?: string | null;
  customerId?: string | null;
  customerType?: string | null;
  customerName?: string | null;
}

/** An empty reading, for a note that carries nothing beyond its own text. */
function noMentions(): DetectedMentions {
  return { subject: '', summary: '', contacts: [], accounts: [], products: [], keyMessages: [], objectives: [] };
}

/**
 * The note as its own reading: the title names the meeting and the transcript is
 * the recap, so what reaches the meeting is the rep's own words rather than a
 * paraphrase of them.
 */
export function seedFromNote(note: NoteSeedSource | null | undefined): DetectedMentions {
  return {
    ...noMentions(),
    subject: (note?.name ?? '').trim(),
    summary: (note?.transcript ?? '').trim(),
  };
}

/**
 * Whether a reading that was asked for came back with nothing to show.
 *
 * The detector answers null both for a transcript it could make nothing of and
 * for one it never looked at. Only the first is worth telling the rep about: the
 * review opens seeded from their own words, under a heading that otherwise reads
 * as a written summary.
 */
export function readingCameBackEmpty(
  asked: boolean,
  detected: DetectedMentions | null | undefined,
): boolean {
  return asked && !detected;
}

/** The detector's reading where there is one, and the note itself where there is not. */
export function detectionForReview(
  detected: DetectedMentions | null | undefined,
  note: NoteSeedSource | null | undefined,
): DetectedMentions {
  return detected ?? seedFromNote(note);
}

/** A note as the combiner reads it: what was said, and when it was said. */
export interface DictatedNote extends NoteSeedSource {
  noteDate?: Date | null;
}

/**
 * Every note recorded against one meeting, read as a single dictation.
 *
 * A call is dictated in more than one sitting — between rooms, on two devices, and
 * again on the way out — and each sitting is only part of what happened. Analysed
 * separately they produce several partial readings of the same call and a review for
 * each. Oldest first, because that is the order the call ran in, and separated by a
 * blank line so the close of one note does not run into the opening of the next.
 */
export function combineNoteTranscripts(notes: DictatedNote[] | null | undefined): string {
  return (notes ?? [])
    .filter((note) => (note?.transcript ?? '').trim())
    .sort((a, b) => (a.noteDate?.getTime() ?? 0) - (b.noteDate?.getTime() ?? 0))
    .map((note) => (note.transcript ?? '').trim())
    .join('\n\n');
}

/**
 * The customer the note already names. It comes from the note's own lookup, so
 * it is a record id rather than a spoken name and never needs matching.
 */
export function knownCustomerOf(note: NoteSeedSource | null | undefined): KnownCustomer | null {
  const id = (note?.customerId ?? '').trim();
  const type = (note?.customerType ?? '').trim();
  if (!id || !type) {
    return null;
  }
  return {
    kind: type === 'contact' ? 'contact' : 'account',
    record: { id, name: (note?.customerName ?? '').trim() },
  };
}
